import { isFuture } from 'date-fns';
import { useResource } from 'datx-jsonapi-react';
import { FC } from 'react';

import { LectureLink } from '@/components/event/LectureLink';
import { formatTimezoneDuration } from '@/components/event/utils';
import { LectureCard } from '@/components/ui/shared/lecture/LectureCard/LectureCard';
import { LectureCardImage } from '@/components/ui/shared/lecture/LectureCardImage/LectureCardImage';
import { LectureCardUpcomingImagePlaceholder } from '@/components/ui/shared/lecture/LectureCardImage/LectureCardUpcomingImagePlaceholder';
import { useUserEventTimezone } from '@/hooks/useUserEventTimezone';
import { queryThinEvent } from '@/queries/thin-events';
import { ThinLecture } from '@/resources/ThinLecture';
import { LECTURE_CARD_IMAGE_FALLBACK } from '@/utils/constants';
import { isEnabled } from '@/utils/models/lecture';

interface ILabeledLectureCardProps {
  lecture: ThinLecture;
  eventId: string;
  profilesEnabled?: boolean;
}

export const LabeledLectureCard: FC<ILabeledLectureCardProps> = ({ lecture, eventId, profilesEnabled }) => {
  const userEventTimezone = useUserEventTimezone(eventId);
  const { data: event } = useResource(queryThinEvent(eventId));

  const isUpcomingHeader = !lecture.publishedAt || isFuture(new Date(lecture.publishedAt));

  const isPoster = Boolean(lecture.posterLecture);

  const posterThumbnail = lecture.mediumPosterDocumentThumbnailUrl || lecture.mediumPosterDocumentThumbnailExtractUrl;

  const imageSrc = isPoster
    ? posterThumbnail || lecture.mediumPosterUrl || event.mediumPosterFallbackImageUrl
    : lecture.mediumPosterUrl || posterThumbnail || event.mediumVideoFallbackImageUrl;

  const lectureImage = isUpcomingHeader ? (
    <LectureCardUpcomingImagePlaceholder publishedAt={lecture.publishedAt} />
  ) : (
    <LectureCardImage src={imageSrc || LECTURE_CARD_IMAGE_FALLBACK} alt={lecture.title} />
  );

  return (
    <LectureLink
      key={lecture.id}
      eventId={eventId}
      sessionId={lecture?.eventSession?.id}
      linkSlug={lecture.linkSlug}
      disabled={!isEnabled(lecture)}
      isPoster={isPoster}
    >
      <LectureCard
        w="full"
        image={lectureImage}
        name={lecture.title}
        authors={lecture.sortedProfiles}
        gatherTownUrl={lecture.gatherTownUrl}
        heldAt={formatTimezoneDuration(lecture.heldAt, userEventTimezone)}
        displayTime={lecture?.eventSession?.displayLecturesWithTime || false}
        profilesEnabled={profilesEnabled}
      />
    </LectureLink>
  );
};
